import React, { useState, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import GuestLayout from "../../Layouts/GuestLayout";
import { TextField } from "@mui/material";
import SecondaryButton from "../../Components/SecondaryButton";
import { useNavigate, Link } from "react-router-dom";

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleEmailChange = useCallback((e) => {
    setEmail(e.target.value);
    if (emailError) setEmailError('');
  }, [emailError]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setEmailError('');

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    let hasError = false;

    if (!email) {
      setEmailError('The email field is empty.');
      hasError = true;
    } else if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email.');
      hasError = true;
    }

    if (hasError) return;

    setIsLoading(true); // inicia loading

    try {
      const { data } = await axios.post(
        'https://new.cajuu.app/api/password/email',
        { email }
      );

      await Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: data.message,
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK',
      });

      navigate('/login');
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        'An error occurred while trying to send the email.';

      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: errorMessage,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Try Again',
      });
    } finally {
      setIsLoading(false); // encerra loading
    }
  };

  return (
    <GuestLayout>
      <div className="flex w-full justify-center mb-[3rem]">
        <div className="flex flex-col items-center text-3xl tracking-tight text-amber-500 font-semibold">
          <span>Forgot Password</span>
          <div className="text-white flex items-center mt-2">
            <img src="/img/cajuu.png" alt="Cajuu Logo" className="w-9 h-9 mr-2" />
            Cajuu<span className="text-[#2f2f2f]/40">.</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div>
          <TextField
            id="email"
            label="Email"
            className="w-full"
            color="secondary"
            value={email}
            onChange={handleEmailChange}
            error={!!emailError}
            helperText={emailError}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderColor: 'red !important',
              },
              '& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderColor: 'currentColor',
              },
            }}
          />
        </div>

        <div className="mt-4 text-sm text-white">
          Enter your email and we'll send you a link to reset your password.
        </div>

        <div className="mt-10">
          <SecondaryButton
            type="submit"
            disabled={isLoading}
            className={`bg-amber-500 hover:bg-amber-600 hover:text-white border-none h-[3rem] text-slate-900 w-full justify-center transition-transform duration-200 ease-in-out hover:scale-95 active:scale-95 ${
              isLoading ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
                Loading...
              </div>
            ) : (
              'Send Reset Link'
            )}
          </SecondaryButton>
        </div>
      </form>

      <div className="mt-6 text-center">
        <Link
          to="/login"
          className="underline text-sm font-medium text-white hover:text-amber-500"
        >
          Back to Login
        </Link>
      </div>
    </GuestLayout>
  );
}